// MyCommunications — donor portal inbox (CHG-1521 wave 3).
//
// /my-giving/communications. Consumes GET /api/v1/my-giving/communications.
// Read-only inbox of receipts/reminders/updates the donor has been sent
// by the orgs they support. Columns: Date, Message (template label),
// Channel (email/sms), Status (Delivered/Pending), Subject.

import type { ReactNode } from "react";
import { ListPage, type ListPageConfig } from "../components/composition/ListPage";
import {
  meCommunicationsApi,
  type MyCommunication,
} from "../api/me-communications";

const STATUS_LABEL: Record<string, string> = {
  sent: "Delivered",
  pending: "Pending",
  failed: "Failed",
};

const CHANNEL_LABEL: Record<string, string> = {
  email: "Email",
  sms: "SMS",
  push: "Push",
  postal: "Postal",
  in_app: "In-app",
};

function statusColumn(row: MyCommunication): ReactNode {
  return STATUS_LABEL[row.status] ?? row.status;
}

function channelColumn(row: MyCommunication): ReactNode {
  return CHANNEL_LABEL[row.channel] ?? row.channel;
}

function subjectColumn(row: MyCommunication): ReactNode {
  return row.subject ?? "—";
}

const myCommunicationsListConfig: ListPageConfig<MyCommunication> = {
  title: "My communications",
  subtitle:
    "Receipts, reminders, and updates the organizations you support have sent you.",
  queryKey: ["me", "communications", "list"] as const,
  queryFn: ({ page, pageSize, sortField, sortDirection, search }) =>
    meCommunicationsApi.listMyCommunications({
      page,
      pageSize,
      sortField,
      sortDirection,
      search,
    }),
  defaultSort: { field: "sent_at", direction: "desc" },
  pageSize: 25,
  emptyState: {
    title: "No messages yet",
    subtitle:
      "Donation receipts, recurring reminders, pledge updates, and announcements from the organizations you support will appear here.",
  },
  columns: [
    {
      field: "sent_at",
      label: "Date",
      type: "relative-date",
      sortable: true,
    },
    {
      field: "template_display_name",
      label: "Message",
      type: "text",
    },
    {
      field: "channel",
      label: "Channel",
      type: "custom",
      render: channelColumn,
    },
    {
      field: "status",
      label: "Status",
      type: "custom",
      sortable: true,
      render: statusColumn,
    },
    {
      field: "subject",
      label: "Subject",
      type: "custom",
      render: subjectColumn,
    },
  ],
};

export function MyCommunications() {
  return <ListPage config={myCommunicationsListConfig} />;
}
